import React, { useEffect, useState } from 'react';
import { SyncWithOutlookButton } from '@/components/shared';
import Spinner from '@/components/shared/Spinner';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import { OutlookSyncState } from '@/lib/outlookSyncManager';
import { BatchProgress } from '@/lib/uploadProgressManager';
import { backendClient } from '@/lib/api-client';
import { Mail, Check } from 'lucide-react';
import * as HoverCard from '@radix-ui/react-hover-card';
import { useNavigate } from '@tanstack/react-router';

interface OutlookSyncSectionProps {
  matterId: string;
  folderId?: number;
  onSyncComplete?: () => void;
  hasCorrespondence?: boolean;
  hideConnectButton?: boolean;
}

export const OutlookSyncSection: React.FC<OutlookSyncSectionProps> = ({
  matterId,
  folderId,
  onSyncComplete,
  hasCorrespondence = false,
  hideConnectButton = false
}) => {
  const navigate = useNavigate();
  // Get sync state from BackgroundSyncContext (background sync now handles all syncing)
  const { getState } = useBackgroundSync();
  const syncState = getState<OutlookSyncState>('outlook-sync');
  const status = syncState?.status || 'idle';

  // Get upload progress state to check for active Outlook sync uploads
  const uploadProgress = getState<BatchProgress[]>('file-upload-progress') || [];

  // Check if there's an active Outlook sync upload in the upload manager
  const hasActiveOutlookUpload = uploadProgress.some(batch =>
    batch.source === 'outlook' &&
    batch.matterGuid === matterId &&
    (batch.status === 'uploading' || batch.status === 'processing')
  );

  // Icon shows loading if there's an active upload, otherwise shows green check
  const isSyncing = hasActiveOutlookUpload;

  const [isOutlookSynced, setIsOutlookSynced] = useState<boolean | null>(true); // Optimistically show as synced
  const [prevHasActiveUpload, setPrevHasActiveUpload] = useState<boolean>(false);

  // Check if matter is synced with Outlook
  useEffect(() => {
    async function checkOutlookSyncStatus() {
      if (!matterId) return;

      try {
        const endpoint = `/correspondence/${matterId}/is-outlook-synced${folderId ? `?destination_folder_id=${folderId}` : ''}`;
        const response = await backendClient.get(endpoint);
        setIsOutlookSynced(response.data.is_outlook_synced);
      } catch (error) {
        console.error('Error checking Outlook sync status:', error);
        setIsOutlookSynced(false);
      }
    }

    checkOutlookSyncStatus();
  }, [matterId, folderId]);

  // Handle sync completion - trigger onSyncComplete callback when upload transitions from active to completed
  useEffect(() => {
    // Detect transition from syncing to not syncing (upload completed)
    if (prevHasActiveUpload && !hasActiveOutlookUpload) {
      if (onSyncComplete) {
        onSyncComplete();
      }
    }
    setPrevHasActiveUpload(hasActiveOutlookUpload);
  }, [hasActiveOutlookUpload, onSyncComplete, prevHasActiveUpload]);

  // Show sync button if not synced with Outlook
  if (isOutlookSynced === false) {
    if (hideConnectButton) {
      return null;
    }

    return (
      <SyncWithOutlookButton
        matterId={matterId}
      />
    );
  }

  // Only show icon if matter is synced with Outlook
  if (isOutlookSynced === true) {
    return (
      <HoverCard.Root openDelay={300} key={`outlook-sync-${isSyncing}-${status}`}>
        <HoverCard.Trigger asChild>
          <button
            className="cursor-pointer focus:outline-hidden flex items-center justify-center lg:pr-2"
            onClick={() => navigate({ to: '/matter/$slug/sync', params: { slug: matterId } })}
          >
            <div className="relative">
              <Mail className="h-6 w-6 text-gray-600" />
              {!isSyncing && hasCorrespondence && (
                <div className="absolute -bottom-1 -right-1 bg-white rounded-full">
                  <Check className="h-4 w-4 text-green-500" strokeWidth={3} />
                </div>
              )}
              {isSyncing && (
                <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5">
                  <Spinner size="xs" />
                </div>
              )}
            </div>
          </button>
        </HoverCard.Trigger>
        <HoverCard.Portal>
          <HoverCard.Content
            className="z-50 w-80"
            sideOffset={5}
          >
            <div className="px-3 py-2 rounded-md border bg-gray-50 border-gray-200 shadow-lg">
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">
                  {isSyncing ? "Syncing with Outlook..." : "Synced with Outlook"}
                </span>
                <span className="text-xs text-gray-500">
                  {isSyncing
                    ? "Background sync in progress"
                    : "Your Outlook folder is up to date"
                  }
                </span>
              </div>
            </div>
          </HoverCard.Content>
        </HoverCard.Portal>
      </HoverCard.Root>
    );
  }

  return null;
};